import { Grid } from '@cmg/design-system';
import {
  FormikAutocompleteField,
  FormikTextField,
  parseFormikValueToSelectOption,
  parseSelectOptionToFormikValue,
} from '@cmg/design-system-formik';
import { xcSelectors } from '@cmg/e2e-selectors';
import { useFormikContext } from 'formik';
import defaultTo from 'lodash/defaultTo';
import React from 'react';

import { useIsSettlementAgentEnabledOnOffering } from '../../../../common/hooks/useIsSettlementAgentEnabledOnOffering/useIsSettlementAgentEnabledOnOffering';
import { OfferingType } from '../../../../graphql';
import { sectorOptions } from '../../../constants';
import { OfferingSetup_BasicInfoRoute_OfferingFullPartsFragment } from '../../graphql';
import type { FormType } from '../BasicInfoForm.model';
import { useCheckPrivatePlacementTrait } from '../hooks/useCheckPrivatePlacementTrait';
import { useGetExcludedOfferingTypes } from '../hooks/useGetExcludedOfferingTypes';
import { defaultParseFormikValue, getOfferingSetupTypesOptions } from './IssuerSubForm.model';
import OfferingSettlementAgentAuthoringBanner from './OfferingSettlementAgentAuthoringBanner';

export type Props = {
  offering: OfferingSetup_BasicInfoRoute_OfferingFullPartsFragment | null;
  submitting?: boolean;
  isInternalOffering: boolean | null;
  countryCode: string;
};

/**
 * A reusable subsection of Fields for Formik forms
 * Used by BasicInfoForm & CreateOfferingForm
 */
export const IssuerSubForm: React.FC<Props> = ({
  submitting,
  offering,
  isInternalOffering,
  countryCode,
}) => {
  const excludedOfferingTypeOptions = useGetExcludedOfferingTypes({
    offeringType: defaultTo(offering?.type, null),
    countryCode,
  });
  const { isSettlementAgentEnabled, loading: isSettlementAgentLoading } =
    useIsSettlementAgentEnabledOnOffering(countryCode);
  const hasPrivatePlacementTrait = useCheckPrivatePlacementTrait();
  const { values } = useFormikContext<FormType>();
  const isPrivatePlacement = values.type === OfferingType.PrivatePlacement;

  return (
    <React.Fragment>
      <Grid container item spacing={2}>
        {!offering &&
          !isInternalOffering &&
          !isSettlementAgentLoading &&
          isSettlementAgentEnabled && (
            <Grid item xs={12} mt={2} marginTop={0}>
              <OfferingSettlementAgentAuthoringBanner />
            </Grid>
          )}
        <Grid item md={3} sm={6} xs={12}>
          <span>
            <FormikAutocompleteField
              data-testid={xcSelectors.offeringSetupBasicInfoOfferingType.testId}
              name="type"
              label="Offering Type"
              disableClearable
              required
              parseFormikValue={defaultParseFormikValue}
              parseToFormikValue={parseSelectOptionToFormikValue}
              placeholder="Choose Offering Type..."
              disabled={submitting || hasPrivatePlacementTrait}
              options={getOfferingSetupTypesOptions({
                exclude: excludedOfferingTypeOptions,
              })}
              fullWidth
            />
          </span>
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <FormikTextField
            name="issuer.name"
            label="Issuer Name"
            placeholder="Enter Name..."
            disabled={submitting}
            fullWidth
            required
          />
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <FormikAutocompleteField
            data-testid={xcSelectors.offeringSetupBasicInfoSector.testId}
            label="Sector"
            placeholder="Choose Sector..."
            name="issuer.sector"
            parseFormikValue={parseFormikValueToSelectOption}
            parseToFormikValue={parseSelectOptionToFormikValue}
            disabled={submitting}
            fullWidth
            options={sectorOptions}
          />
        </Grid>
        {isPrivatePlacement && (
          <Grid item md={3} sm={6} xs={12}>
            <FormikTextField
              name="issuer.subSector"
              label="Sub-Sector"
              placeholder="Enter Sub-Sector..."
              disabled={submitting}
              fullWidth
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <FormikTextField
            name="issuer.businessDescription"
            placeholder="Enter Business Description..."
            disabled={submitting}
            label="Business Description"
            multiline
            rows={4}
            fullWidth
          />
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default IssuerSubForm;
